import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Heart, Lock, Eye, EyeOff, Loader2, CheckCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import API_URL from '../config/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!passwordRequirements.every(req => req.met)) {
      setError('Please meet all password requirements');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Something went wrong';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <h1 className="heading-2 mb-4">Invalid Link</h1>
          <p className="body-text text-[#6C6C67] mb-8">
            This password reset link is invalid or has expired.
          </p>
          <Link to="/forgot-password">
            <Button className="btn-primary" data-testid="request-new-link-btn">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#2D6A4F]" />
          </div>
          <h1 className="heading-2 mb-4">Password Reset!</h1>
          <p className="body-text text-[#6C6C67] mb-8">
            Your password has been successfully reset. You can now login with your new password.
          </p>
          <Link to="/login">
            <Button className="btn-primary" data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="reset-password-logo">
          <Heart className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
          <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </Link>
        
        <h1 className="heading-2 mb-2">Reset Password</h1>
        <p className="body-text text-[#6C6C67] mb-8">
          Enter your new password below.
        </p>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm" data-testid="reset-password-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="label-text block mb-2">New Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-3 bg-white border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] transition-colors"
                placeholder="••••••••"
                required
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6C6C67] hover:text-[#2D2D2A]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <div className="mt-3 space-y-1">
              {passwordRequirements.map((req) => (
                <div key={req.text} className={`flex items-center gap-2 text-sm ${req.met ? 'text-[#2D6A4F]' : 'text-[#6C6C67]'}`}>
                  <Check className={`w-4 h-4 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                  {req.text}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <label className="label-text block mb-2">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] transition-colors"
                placeholder="••••••••"
                required
                data-testid="reset-confirm-password-input"
              />
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={loading}
            data-testid="reset-password-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
